import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { listClients } from '../services/clientsService';
import { friendlyError } from '../lib/errors';
import DataTable from '../components/ui/DataTable';
import SearchInput from '../components/ui/SearchInput';
import Pagination from '../components/ui/Pagination';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import Badge from '../components/ui/Badge';

export default function ClientsPage() {
  const { ruc } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  const fetchPage = useCallback(
    async ({ reset = false } = {}) => {
      if (!ruc) return;
      if (reset) setLoading(true);
      else setLoadingMore(true);
      setError(null);
      try {
        const result = await listClients({ ruc, search, cursor: reset ? null : cursor });
        setItems((prev) => (reset ? result.items : [...prev, ...result.items]));
        setCursor(result.cursor || null);
        setHasMore(!!result.hasMore);
      } catch (e) {
        setError(friendlyError(e));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [ruc, search, cursor]
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setItems([]);
      setCursor(null);
      fetchPage({ reset: true });
    }, search ? 280 : 0);
    return () => clearTimeout(t);
  }, [ruc, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = [
    {
      key: 'nroDocumento',
      label: 'Documento',
      render: (r) => (
        <span>
          <Badge tone="neutral">{r.tipoDocumento || '—'}</Badge> {r.nroDocumento || '—'}
        </span>
      ),
    },
    {
      key: 'name',
      label: 'Cliente',
      render: (r) => r.razonSocial || r.nombre || r.nombreComercial || '—',
    },
    { key: 'direccion', label: 'Dirección', render: (r) => r.direccion || '—' },
    { key: 'telefono', label: 'Teléfono', render: (r) => r.telefono || '—' },
    {
      key: 'scope',
      label: 'Ámbito',
      render: (r) =>
        r.ruc === 'GLOBAL' || r.isGlobal ? <Badge tone="info">Global</Badge> : <Badge tone="ok">Empresa</Badge>,
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (r) => (
        <div className="row-actions">
          <button
            type="button"
            className="btn btn-mini"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/clientes/${r.id}`);
            }}
          >
            Ver
          </button>
          {r.ruc !== 'GLOBAL' ? (
            <button
              type="button"
              className="btn btn-mini btn-ghost"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/admin/clientes/${r.id}/editar`);
              }}
            >
              Editar
            </button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Clientes</h1>
          <p className="muted">Maestro de clientes · incluye catálogo GLOBAL · lazy limit 10</p>
        </div>
        <Link className="btn btn-primary" to="/admin/clientes/nuevo">
          Nuevo cliente
        </Link>
      </header>

      <div className="toolbar">
        <SearchInput value={search} onChange={setSearch} placeholder="Buscar por documento o nombre…" />
      </div>

      {loading ? (
        <LoadingState label="Cargando clientes…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => fetchPage({ reset: true })} />
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={items}
            keyField="id"
            onRowClick={(row) => navigate(`/admin/clientes/${row.id}`)}
            empty={<EmptyState title="No hay clientes" description="Crea el primero o usa los de la app móvil." />}
          />
          <Pagination hasMore={hasMore} onMore={() => fetchPage()} loading={loadingMore} shown={items.length} />
        </>
      )}
    </div>
  );
}
